
/**
 * Utility for validating GEO (Generative Engine Optimization) metadata.
 * Checks for:
 * 1. Presence of Citations (Boosts Authority/E-E-A-T)
 * 2. Presence of Entity Mentions (Boosts Relevance)
 * 3. Relevance of Mentions (Ensures tagged entities actually appear in content to avoid spam penalties)
 */

export interface GeoIssue {
    message: string;
    items?: string[]; // For rendering bullet lists
    type: 'warning' | 'success';
}

export const validateGeoMetadata = (
    citations: string[] | undefined | null,
    mentions: string[] | undefined | null,
    content?: string
): GeoIssue[] => {
    const warnings: GeoIssue[] = [];

    // 1. Quantity/Presence Checks
    if (!citations || citations.length === 0) {
        warnings.push({
            message: "⚠️ Missing Citations: You haven't added any Sources. Linking to 3-5 authoritative sources strongly boosts your E-E-A-T score.",
            type: 'warning'
        });
    }

    if (!mentions || mentions.length === 0) {
        warnings.push({
            message: "⚠️ Missing Entities: You haven't tagged any Entities (Mentions). Helping AI identify key subjects improves your Relevance score.",
            type: 'warning'
        });
    }

    // 2. Quality/Relevance Check
    if (mentions && mentions.length > 0 && content) {
        const contentLower = content.toLowerCase();
        const irrelevantMentions: string[] = [];

        mentions.forEach((mention) => {
            // Simple case-insensitive inclusion check
            if (!contentLower.includes(mention.toLowerCase())) {
                irrelevantMentions.push(mention);
            }
        });

        if (irrelevantMentions.length > 0) {
            warnings.push({
                message: "⚠️ Relevancy Issue: The following entities were tagged but NOT found in your content. Only tag entities that are actually discussed in the article to avoid \"Metadata Stuffing\" penalties.",
                items: irrelevantMentions,
                type: 'warning'
            });
        }
    }

    return warnings;
};
